import { humanFileSize, mapFileExtensionToIcon, truncateFileName } from '@/shared/helpers/file-helper';
import { cn } from '@/shared/utilities';
import { Close, Download } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useDownload } from '../../hooks/use-download';
import type { FileAttachment } from '../../models/attachment';

const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex !== -1 ? filename.substring(lastDotIndex + 1) : '';
};

interface FileAttachmentItemProps {
  attachment: FileAttachment;
  onRemove?: (attachmentId: string) => void;
  maxFileNameLength: number;
  mode: 'compact' | 'comfortable';
}

export const FileAttachmentItem: React.FC<FileAttachmentItemProps> = React.memo(
  ({ attachment, onRemove, maxFileNameLength, mode }) => {
    const isCompact = mode === 'compact';

    const extension = getFileExtension(attachment.filename || '');
    const truncatedName = truncateFileName(attachment.filename || 'Unknown file', maxFileNameLength);
    const { mutate: downloadFile, isPending } = useDownload(attachment.url || '', attachment.filename || 'download');

    return (
      <div
        className={cn(
          'mex:group/file mex:hover:!bg-action-hover mex:flex mex:items-center mex:gap-3 mex:p-3 mex:transition-colors',
          'mex:dark:hover:bg-primary-900/20',
          isCompact && 'mex:max-w-[200px]'
        )}
      >
        <Box className='mex:flex-shrink-0'>{mapFileExtensionToIcon(extension, { fontSize: 24 })}</Box>

        <Box className='mex:min-w-0 mex:flex-1'>
          <Typography
            variant='body2'
            sx={{ fontWeight: 500 }}
            className='mex:truncate mex:text-gray-900 mex:dark:text-gray-100'
            title={attachment.filename}
          >
            {truncatedName}
          </Typography>
          {attachment.size && (
            <Typography variant='caption' color='text.secondary' className='mex:dark:text-gray-400'>
              {humanFileSize(attachment.size)}
            </Typography>
          )}
        </Box>
        {isPending ? (
          <CircularProgress size={20} color='primary' />
        ) : (
          <IconButton
            size='medium'
            disabled={isPending}
            onClick={() => downloadFile()}
            className='mex:!hidden mex:group-hover/file:!flex mex:flex-shrink-0'
          >
            <Download className='mex:!size-5' />
          </IconButton>
        )}

        {onRemove && (
          <IconButton
            size='small'
            onClick={() => onRemove(attachment.id || '')}
            className='mex:flex-shrink-0 mex:text-gray-400 mex:transition-colors mex:hover:text-red-500 mex:dark:text-gray-500 mex:dark:hover:text-red-400'
            sx={{
              '&:hover': {
                backgroundColor: 'error.lighter'
              }
            }}
          >
            <Close className='mex:!size-4' />
          </IconButton>
        )}
      </div>
    );
  }
);

interface ComposeFilesProps {
  attachments?: FileAttachment[];
  className?: string;
  onRemove?: (attachmentId: string) => void;
  maxFileNameLength?: number;
  mode?: 'compact' | 'comfortable';
}

export const ComposeFiles: React.FC<ComposeFilesProps> = ({
  attachments = [],
  className,
  onRemove,
  maxFileNameLength = 30,
  mode = 'comfortable'
}) => {
  if (attachments.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'mex:!rounded-lg mex:w-full mex:divide-y mex:divide-gray-300 mex:dark:divide-primary-900/20',
        className
      )}
    >
      {attachments.map((attachment, index) => (
        <FileAttachmentItem
          key={attachment.id || index}
          attachment={attachment}
          onRemove={onRemove}
          maxFileNameLength={maxFileNameLength}
          mode={mode}
        />
      ))}
    </div>
  );
};
